import * as React from "react";
import { CalendarDimensions } from "svg/constants";
import { TextInformation, CalendarState, BurdeFontResponse } from "burde";
import { actionCreators } from "page/calendar/store";
import { actionCreators as uiActionCreators } from "ui/store";
import CoverTextInputItem from "layout/common/controls-area/cover-text-input/cover-text-input-item";
import Button from "content/button/button";
import { ZentaColorPickerItem } from "../zenta-color-picker";
import { removeEmojis } from "ui/constants";

const validTextColors: Array<ZentaColorPickerItem> = [
    { id: 1, hexColor: "#FFFFFF" },
    { id: 2, hexColor: "#000000" },
    { id: 3, hexColor: "#57585A" },
    { id: 4, hexColor: "#CFD1D2" },
    { id: 5, hexColor: "#338C7D" },
    { id: 6, hexColor: "#A6D6C5" },
    { id: 7, hexColor: "#80DDDD" },
    { id: 8, hexColor: "#1F5FCA" },
    { id: 9, hexColor: "#B2E0F4" },
    { id: 10, hexColor: "#E4CAEA" },
    { id: 11, hexColor: "#FFC5D4" },
    { id: 12, hexColor: "#FF8ED1" },
    { id: 13, hexColor: "#EA2626" },
    { id: 14, hexColor: "#F5C24E" },
];

type CoverTextInputProps = {
    Cover_Text_AddText: string;
    Cover_Text_Placeholder: string;
    Enable_Text_Rotation: string;
    calendar: CalendarState;
    fontOptions: BurdeFontResponse | null;
} & typeof actionCreators &
    typeof uiActionCreators;

class CoverTextInput extends React.Component<CoverTextInputProps> {
    updateText = (value: string, index: number) => {
        //Remove emojis from the string
        let cleanedValue = removeEmojis(value);
        const texts = [...this.props.calendar.coverTexts];
        texts[index].text = cleanedValue;
        this.props.updateCoverTextCollection(texts);
    };

    updateRotation = (value: number, index: number) => {
        const texts = [...this.props.calendar.coverTexts];
        texts[index].rotation = value;
        this.props.updateCoverTextCollection(texts);
    };

    updateColor = (value: any, index: number) => {
        const selectedColor = validTextColors.find((x) => x.id === value);

        if (!selectedColor) {
            return;
        }

        const texts = [...this.props.calendar.coverTexts];
        texts[index].color = selectedColor.hexColor;
        this.props.updateCoverTextCollection(texts);
    };

    updateCustomColor = (value: string, index: number) => {
        const texts = [...this.props.calendar.coverTexts];
        texts[index].color = value;

        this.props.updateCoverTextCollection(texts);
    };

    updateFont = (value: number, index: number) => {
        const texts = [...this.props.calendar.coverTexts];
        texts[index].fontId = value;
        // Remove legacy font on any selection.
        texts[index].fontSetName = null;

        this.props.updateCoverTextCollection(texts);
    };

    updateFontSize = (value: string, index: number) => {
        const texts = [...this.props.calendar.coverTexts];
        texts[index].fontSize = value;

        this.props.updateCoverTextCollection(texts);
    };

    addText = () => {
        const texts = [...this.props.calendar.coverTexts];

        const calendarSize =
            this.props.calendar.calendarSize != ""
                ? this.props.calendar.calendarSize
                : "A5";
        const calendarDimensions = CalendarDimensions.find(
            (x) => x.calendarSize === calendarSize,
        );

        if (calendarDimensions == undefined) {
            return;
        }

        let textInfo: TextInformation = {
            color: "#57585A",
            text: "",
            fontSize: calendarDimensions.initialCoverTextFontSize,
            x: calendarDimensions.initialCoverTextX,
            y: calendarDimensions.initialCoverTextY,
            rotation: 0,
            fontId: this.props.fontOptions ? this.props.fontOptions.default : 0,
            fontSetName: null,
        };

        texts.push(textInfo);

        this.props.updateCoverTextCollection(texts);
    };

    removeText = (text: TextInformation) => {
        var textList = this.props.calendar.coverTexts.filter(
            (obj) => obj !== text,
        );

        this.props.updateCoverTextCollection(textList);
    };

    componentDidUpdate(prevProps: CoverTextInputProps) {
        if (
            this.props.fontOptions &&
            JSON.stringify(prevProps.fontOptions) !==
                JSON.stringify(this.props.fontOptions)
        ) {
            // If a selected font doesn't exist within the available fonts, change it to default ID.
            var discrepancyFound = false;
            const checkedCoverTexts = this.props.calendar.coverTexts.map(
                (textItem) => {
                    if (this.props.fontOptions !== null) {
                        const match = this.props.fontOptions.fontResources.find(
                            (x) => x.id === textItem.fontId,
                        );
                        if (!match) {
                            discrepancyFound = true;
                            textItem.fontId = this.props.fontOptions.default;
                        }
                    }
                    return textItem;
                },
            );
            if (discrepancyFound) {
                this.props.updateCoverTextCollection(checkedCoverTexts);
            }
        }
    }

    public render() {
        const { Cover_Text_AddText, Cover_Text_Placeholder } = this.props;

        return (
            <React.Fragment>
                <div
                    style={{ paddingTop: "10px" }}
                    key={"text-input-text-fields"}>
                    {this.props.calendar.coverTexts.map((text, index) => (
                        <CoverTextInputItem
                            key={index}
                            index={index}
                            textItem={text}
                            textColors={validTextColors}
                            removeTextItem={this.removeText}
                            updateText={this.updateText}
                            updateFontSize={this.updateFontSize}
                            updateFont={this.updateFont}
                            updateTextColor={this.updateColor}
                            updateRotation={this.updateRotation}
                            enableTextRotationLabel={
                                this.props.Enable_Text_Rotation
                            }
                            updateCustomTextColor={this.updateCustomColor}
                            placeholderText={Cover_Text_Placeholder}
                            fontOptions={this.props.fontOptions}
                        />
                    ))}
                </div>
                <div
                    className={"col-xs-12"}
                    key={"text-input-add-text"}
                    style={{ paddingBottom: "20px" }}>
                    <div className={"col-xs-1"}>
                        <Button
                            text={Cover_Text_AddText}
                            width={"100%"}
                            onClick={() => {
                                this.addText();
                            }}
                        />
                    </div>
                </div>
            </React.Fragment>
        );
    }
}

export default CoverTextInput;
